import { useUser } from 'Core/Hooks/useUser'
import { BulkDeleteSelectionModal, FloatingActions, SearchInput } from 'V2Components'
import { useApplicationAccess } from 'features/applications'
import { IAMResourcesSliceNames, useIAMRsrcAccess } from 'features/iamResources'
import { createRsrcKey, getResourceName, reverseRsrcKey } from 'features/resources'
import { TargetsSliceNames, useTargetAccess } from 'features/targets'
import {
  BulkAddToGroup,
  getUserDisplayName,
  getUserGroupsRefs,
  getUserOwnedDevices,
  getUserSource,
  getUserStatus,
  isAdminUser
} from 'features/users'
import { createDataSelectorHook, useObjectRef, useReduxFetch } from 'infra/redux'
import { ResponsiveTable } from 'infra/ui'
import _ from 'lodash'
import moment from 'moment'
import { SimpleDropDown, Typography } from 'procyon-ui'
import React, { useState } from 'react'
import { useHistory } from 'react-router'
import { Header } from './components/Header'
import { UserDetailsModal } from './components/UserDetailsModal'
import { responsiveColumns } from './utils'
const useSlices = createDataSelectorHook(['userList', 'groupList', 'deviceList'])

function Users() {
  const [searchKey, setSearchKey] = useState('')
  const [sortFilter, setSortFilter] = useState('name')
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedRowIDs, setSelectedRowIDs] = useState([])
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false)
  const [showBulkDeleteUserModal, setShowBulkDeleteUserModal] = useState(false)
  const [showBulkAddToGroupModal, setShowBulkAddToGroupModal] = useState(false)
  const [activeUserDetailsTab, setActiveUserDetailsTab] = useState('GROUPS') //GROUPS || DEVICES || RSRCS

  const history = useHistory()
  const { user } = useUser()

  const { slices, sliceNames } = useSlices()

  const { getObjectRef } = useObjectRef(sliceNames)

  const { getPolicySignInURLS } = useIAMRsrcAccess()
  const { getUserAccessTargetsMap } = useTargetAccess()
  const { getUserApplicationAccessMap } = useApplicationAccess()

  const handleUserDetailsGoToClick = (name) => {
    history.push(`/admin/users/${name}`)
  }

  const checkIsAdmin = (u) => {
    return isAdminUser(u)
  }

  /**
   * @param {any} user
   * @param {'GROUPS'|'DEVICES'|'RSRCS'|'APPS'|'TARGETS'} tab
   */
  const handleUserDetailsClick = (user, tab) => {
    setSelectedUser(user)
    setShowUserDetailsModal(true)
    setActiveUserDetailsTab(tab)
  }

  const handleUserDetailsClose = () => {
    setShowUserDetailsModal(false)
    setSelectedUser(null)
  }

  const handleViewProfileClick = (u) => history.push(`/admin/users/${u.ObjectMeta.Name}`)

  const getUserDeviceCount = (u) => getUserOwnedDevices(slices.deviceList, u).length

  const getUserRsrcsCount = (u) => {
    const appsCount = Object.keys(getUserApplicationAccessMap(u)).length
    const targetsCount = Object.keys(getUserAccessTargetsMap(u)).length
    const rsrcsCount = Object.keys(getPolicySignInURLS(u)).length
    return {
      appsCount,
      rsrcsCount,
      targetsCount,
      total: appsCount + rsrcsCount + targetsCount
    }
  }

  const getFilteredUsersList = () => {
    const sortedItems = _.sortBy(slices.userList, (u) =>
      SortByFilterMap[sortFilter](u).toLowerCase()
    )
    return sortedItems.filter((e) => {
      if (searchKey) {
        const searchString = `${e.ObjectMeta.Name}${e.Spec.UserName} ${
          e.Spec.FullName
        } ${getUserSource(e)} ${getUserStatus(e)}`
        return searchString.toLowerCase().includes(searchKey)
      }
      return true
    })
  }

  const tableRowAdapterFn = (u) => {
    return {
      key: createRsrcKey(u),
      objectName: u.Spec.UserName === '' ? u.ObjectMeta.Name : u.Spec.UserName,
      name: {
        // For SCIM users, naming fields might not be there and if it's not there we will show `Empty` label
        name: getUserSource(u) === 'SCIM' ? getUserDisplayName(u, false) : getResourceName(u),
        isLoggedIn: createRsrcKey(user) === createRsrcKey(u)
      },
      userName: u.Spec.UserName === '' ? u.Spec.FullName : u.Spec.UserName,
      groups: {
        count: getUserGroupsRefs(u).length,
        onClick: () => handleUserDetailsClick?.(u, 'GROUPS')
      },
      devices: {
        count: getUserDeviceCount?.(u),
        onClick: () => handleUserDetailsClick?.(u, 'DEVICES')
      },
      rsrcAccess: {
        count: getUserRsrcsCount?.(u),
        onClick: (tab) => handleUserDetailsClick?.(u, tab)
      },
      source: getUserSource(u),
      status: getUserStatus(u),
      type: checkIsAdmin(u) ? 'Admin' : 'User',
      more: {
        onViewProfileClick: () => handleViewProfileClick?.(u)
      },
      details: {
        onClick: () => handleViewProfileClick?.(u)
      },
      goToUserDetails: () => handleUserDetailsGoToClick(u.ObjectMeta.Name)
    }
  }

  const getTableData = () => {
    return getFilteredUsersList().map(tableRowAdapterFn)
  }

  useReduxFetch([
    'deviceList',
    'groupList',
    'policyList',
    ...TargetsSliceNames,
    ...IAMResourcesSliceNames
  ])

  return (
    <div className=''>
      <Header searchKey={searchKey} onSearchKeyChange={setSearchKey} />
      <div className='mt-8 bg-[var(--color-common-bg-base)] p-5'>
        <div className='flex justify-between'>
          <Typography variant='body-regular'>
            {getFilteredUsersList().length} Total Users
          </Typography>
          <div className='flex gap-2'>
            {/* <SearchInput sx={{ width: '303px' }} searchKey={searchKey} onChange={setSearchKey} /> */}
            <SimpleDropDown
              menuItems={SortMenuItems}
              onChange={(e) => setSortFilter(e.target.value)}
              value={sortFilter}
            />
          </div>
        </div>
        <div className='mt-4'>
          <ResponsiveTable scrollX='1200px' columns={responsiveColumns()} data={getTableData()} />
        </div>
      </div>
      {showUserDetailsModal && selectedUser && (
        <UserDetailsModal
          tab={activeUserDetailsTab}
          onClose={handleUserDetailsClose}
          user={selectedUser}
        />
      )}

      {selectedRowIDs.length > 0 && (
        <FloatingActions
          message={`${selectedRowIDs.length} Users Selected`}
          buttons={[
            {
              label: 'Add To Group',
              onClick: () => setShowBulkAddToGroupModal(true),
              variant: 'grayBlue'
            },
            {
              label: 'Delete',
              onClick: () => setShowBulkDeleteUserModal(true),
              variant: 'grayRed'
            }
          ]}
        />
      )}
      {showBulkDeleteUserModal && (
        <BulkDeleteSelectionModal
          items={getObjectRef(selectedRowIDs.map((e) => reverseRsrcKey(e)))}
          onCancel={() => setShowBulkDeleteUserModal(false)}
          onSuccess={() => setShowBulkDeleteUserModal(false)}
          type='User'
        />
      )}
      {showBulkAddToGroupModal && (
        <BulkAddToGroup
          users={getObjectRef(selectedRowIDs.map((e) => reverseRsrcKey(e)))}
          onSuccess={() => setShowBulkAddToGroupModal(false)}
          onCancel={() => setShowBulkAddToGroupModal(false)}
        />
      )}
    </div>
  )
}

/**
 * Constants goes here
 */

const SortByFilterMap = {
  name: (u) => getResourceName(u),
  userName: (u) => u.Spec.UserName,
  created: (u) => moment(u.ObjectMeta.CreatedAt).toDate().getTime().toString()
}

const SortMenuItems = [
  {
    label: 'Sort: by Name',
    value: 'name'
  },
  {
    label: 'Sort: by User Name',
    value: 'userName'
  },
  {
    label: 'Sort: by Created',
    value: 'created'
  }
]

export { Users }
